import React, { useState, useEffect } from 'react';
import { GeneratedImagesGridProps, ImageGridData, LoRAProduct } from './types';
import { Skeleton, Tabs, Tab } from '@nextui-org/react';
import Image from 'next/image';
import { Tooltip } from '@nextui-org/react';
import CopyButton from '@/components/ui/copy-button';
import ComposeText from '@/components/ui/compose-text';
import { convertBackendSize } from '@/utils/image-size';
import { logoPlaceholder } from '@/utils/placeholders';
import { useInView } from 'react-intersection-observer';
import { cn } from '@/lib/utils';
import { useUpdateImagesPublicStatus } from '@/data/ai-image';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useToggleImageLike, useReportImage } from '@/data/ai-image';
import { HeartIcon, Flag } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ButtonOld from '@/components/ui/button-old';
import { Textarea } from '@/components/ui/textarea';
import { trackEvent } from '@/data/utils/utils';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';
import { Loader2 } from 'lucide-react';
import { useNSFWBlur } from '@/lib/hooks/use-nsfw-blur';
import NSFWToggle from '@/components/nsfw/nsfw-toggle';
import { useNSFWSettings } from '@/lib/hooks/use-nsfw-settings';
import Link from 'next/link';
import { useMe } from '@/data/user';

interface ImageGridProps extends ImageGridData {
  setFullscreenImage: (url: string) => void;
  maxHeight?: string;
  isGalleryPage?: boolean;
}

const ImageGrid: React.FC<ImageGridProps> = ({
  images,
  setFullscreenImage,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  maxHeight,
  isGalleryPage,
}) => {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'sm' || breakpoint === 'xs';
  const [visibleImages, setVisibleImages] = useState(3);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    id: number;
    index: number;
    isLiked: boolean;
    prompt: string;
  } | null>(null);
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });
  const { settings } = useNSFWSettings();
  const { shouldBlur } = useNSFWBlur();
  const { me } = useMe();

  // Reset visible images when images array changes
  useEffect(() => {
    setVisibleImages(isMobile ? 2 : images?.length || 0);
  }, [images, isMobile]);

  const { mutate: updatePublicStatus, isLoading: isUpdating } =
    useUpdateImagesPublicStatus({
      onSuccess: (data) => {
        toast.success(
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              Image is now {data.is_public ? 'visible to everyone' : 'private'}
            </span>
          </div>,
          {
            duration: 2000,
            position: 'bottom-center',
          }
        );
      },
      onError: (error) => {
        toast.error(`Failed to update visibility: ${error.message}`);
      },
    });

  const { mutate: toggleLike, isLoading: isLiking } = useToggleImageLike({
    onSuccess: () => {
      toast.success('Preference updated');
    },
    onError: (error) => {
      toast.error(`Failed to update preference: ${error.message}`);
    },
  });

  const { mutate: reportImage, isLoading: isReporting } = useReportImage({
    onSuccess: () => {
      toast.success('Image reported successfully');
      setReportDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to report image: ${error.message}`);
    },
  });

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [selectedReport, setSelectedReport] = useState<{
    id: number;
    index: number;
  } | null>(null);

  useEffect(() => {
    if (
      !isMobile &&
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      onLoadMore
    ) {
      onLoadMore();
    }
  }, [inView, hasNextPage, isFetchingNextPage, onLoadMore, isMobile]);

  const handleLoadMore = () => {
    if (isMobile) {
      setVisibleImages((prev) => Math.min(prev + 3, images?.length || 0));
    } else if (onLoadMore) {
      onLoadMore();
    }
  };

  const handleDownload = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(
        `/api/download?url=${encodeURIComponent(imageUrl)}`
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleImageClick = (url: string, image: any, index: number) => {
    setSelectedImage({
      url,
      id: image.id,
      prompt: image.prompt,
      index,
      isLiked:
        image.my_feedback?.metadata?.liked_image_indexes?.includes(index),
    });
    setFullscreenImage(url);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] w-full flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
        <p className="text-muted-foreground text-sm">Loading images...</p>
      </div>
    );
  }

  if (!isLoading && (!images || images.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-gray-500 dark:text-gray-400">
          No images generated yet.
        </div>
      </div>
    );
  }

  const displayedImages = images?.slice(0, visibleImages);
  const hasMoreImages = images?.length > visibleImages;

  return (
    <div
      className={cn(
        'space-y-6 pr-2 xl:space-y-8',
        !isGalleryPage && maxHeight && !isMobile
          ? 'overflow-y-auto'
          : 'overflow-visible'
      )}
      style={!isGalleryPage && !isMobile ? { maxHeight } : undefined}
    >
      {displayedImages?.map((image) => (
        <div
          key={image.id}
          className="space-y-3 border-b border-gray-100 pb-4 dark:border-dark-400"
        >
          <div className="grid grid-cols-4 gap-1.5 sm:gap-2 xl:gap-4">
            {image.status !== 'success'
              ? [...Array(image.num_images || 4)].map((_, index) => (
                  <Skeleton
                    key={index}
                    className="flex aspect-square w-full items-center justify-center rounded-md bg-gray-200"
                  />
                ))
              : image.image_urls?.map(
                  (url: { original: string }, index: number) => (
                    <div
                      key={url.original}
                      className="group relative cursor-pointer"
                      style={{
                        aspectRatio: convertBackendSize(
                          image.image_size
                        ).replace(':', '/'),
                      }}
                      onClick={() =>
                        handleImageClick(url.original, image, index)
                      }
                    >
                      <Image
                        src={url.original}
                        alt={`Generated ${index + 1}`}
                        fill
                        className="rounded-lg object-cover"
                      />
                      {/* {image.enable_safety_checker &&
                      image.has_nsfw_concepts &&
                      JSON.parse(image.has_nsfw_concepts)[index] == true ? (
                        <div className="absolute inset-0 z-20 flex items-center justify-center">
                          <p className="rounded-lg bg-black/50 px-4 py-2 text-center text-lg font-medium text-white backdrop-blur-sm">
                            Content Not Allowed!
                          </p>
                        </div>
                      ) : null} */}
                      {(() => {
                        try {
                          const nsfwArray =
                            typeof image.has_nsfw_concepts === 'string'
                              ? JSON.parse(image.has_nsfw_concepts)
                              : image.has_nsfw_concepts;
                          const isNSFW =
                            Array.isArray(nsfwArray) && nsfwArray[index];
                          return (
                            isNSFW &&
                            (!settings.mature ||
                              !settings.explicit ||
                              !settings.confirmed18)
                          );
                        } catch (e) {
                          return false;
                        }
                      })() && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 backdrop-blur-md">
                          <div className="bg-dot-pattern absolute inset-0 opacity-5" />
                          <div className="relative flex flex-col items-center gap-2">
                            <div className="mb-1 h-1.5 w-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
                            <p className="text-sm font-medium text-foreground/90">
                              Adult Content
                            </p>
                            <div className="rounded-lg border border-violet-500/20 bg-background/90 px-4 py-2 shadow-lg backdrop-blur-sm">
                              <NSFWToggle size="lg" className="min-w-[120px]" />
                            </div>
                          </div>
                        </div>
                      )}
                      <div
                        className={cn(
                          'absolute inset-0 rounded-lg bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 duration-300 transition-opacity group-hover:opacity-100',
                          !isMobile && 'sm:group-hover:opacity-100'
                        )}
                      >
                        <div
                          className={cn(
                            'absolute bottom-1 left-1 right-1 flex flex-wrap justify-center gap-1 sm:bottom-2 sm:left-2 sm:right-2 sm:gap-2',
                            isMobile && 'hidden'
                          )}
                        >
                          <Tooltip content="Download" placement="top">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(
                                  url.original,
                                  `generated-image-${index + 1}.jpg`
                                );
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white duration-200 transition-colors hover:bg-white/30 sm:h-8 sm:w-8"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                            </button>
                          </Tooltip>

                          <Tooltip
                            content={
                              image.my_feedback?.metadata?.liked_image_indexes?.includes(
                                index
                              )
                                ? 'Unlike'
                                : 'Like'
                            }
                            placement="top"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLike({ id: image.id, imageIndex: index });
                              }}
                              disabled={isLiking}
                              className={cn(
                                'flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white duration-200 transition-colors hover:bg-white/30 sm:h-8 sm:w-8',
                                image.my_feedback?.metadata?.liked_image_indexes?.includes(
                                  index
                                ) && 'text-red-500'
                              )}
                            >
                              <HeartIcon
                                className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                                fill={
                                  image.my_feedback?.metadata?.liked_image_indexes?.includes(
                                    index
                                  )
                                    ? 'currentColor'
                                    : 'none'
                                }
                              />
                            </button>
                          </Tooltip>

                          <Tooltip content="Report" placement="top">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReport({ id: image.id, index });
                                setReportDialogOpen(true);
                              }}
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white duration-200 transition-colors hover:bg-white/30 sm:h-8 sm:w-8"
                            >
                              <Flag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  )
                )}
          </div>
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:gap-4">
            <div className="flex-1 text-sm text-gray-700 dark:text-light-400">
              <ComposeText text={image.prompt} maxHeight={30} />
              {image.product && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-xs text-default-400">
                    Generated with
                  </span>
                  <Link
                    href={`/products/${image.product.slug}`}
                    className="text-xs font-medium text-brand transition-colors hover:text-brand-dark"
                  >
                    {image.product.name}
                  </Link>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {image.additional_lora_products &&
                image.additional_lora_products.length > 0 && (
                  <div className="flex -space-x-2">
                    {image.additional_lora_products.map(
                      (model: LoRAProduct, index: number) => (
                        <Tooltip
                          key={model.id}
                          content={`Used ${model.name} LoRA model`}
                          placement="top"
                        >
                          <div
                            className="relative h-6 w-6 rounded-full border-2 border-white dark:border-dark-200"
                            style={{
                              zIndex: image.additional_lora_products?.length
                                ? image.additional_lora_products.length - index
                                : 0,
                            }}
                          >
                            <Image
                              src={model.image?.original ?? logoPlaceholder}
                              alt={model.name}
                              fill
                              className="rounded-full object-cover"
                            />
                          </div>
                        </Tooltip>
                      )
                    )}
                  </div>
                )}
              <Tooltip content={'Aspect ratio'}>
                <span className="text-xs text-default-500">
                  {convertBackendSize(image.image_size)}
                </span>
              </Tooltip>
              <CopyButton
                value={image.prompt}
                size="md"
                className="bg-default-100 text-default-500 hover:bg-default-200"
              />
              {me?.id === image.user_id && (
                <Tooltip
                  content={image.is_public ? 'Make private' : 'Make public'}
                  placement="top"
                >
                  <button
                    onClick={() => {
                      updatePublicStatus({
                        image_ids: [image.id],
                        is_public: !image.is_public,
                      });
                      trackEvent('toggle_image_visibility', {
                        image_id: image.id,
                        new_status: !image.is_public ? 'public' : 'private',
                      });
                    }}
                    disabled={isUpdating}
                    className={cn(
                      'flex h-8 w-8 min-w-0 items-center justify-center rounded-lg duration-200 transition-colors',
                      image.is_public
                        ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-dark-400 dark:text-light-400 dark:hover:bg-dark-300'
                    )}
                  >
                    {image.is_public ? (
                      <div className="relative">
                        <EyeIcon className="h-4 w-4" />
                        <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green-500" />
                      </div>
                    ) : (
                      <EyeOffIcon className="h-4 w-4" />
                    )}
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      ))}

      {isFetchingNextPage && (
        <div className="flex justify-center py-8">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      )}

      {/* Load more button for mobile or infinite scroll reference for desktop */}
      {(hasMoreImages || (!isMobile && hasNextPage)) && (
        <div className="mt-8 grid place-content-center">
          {isMobile ? (
            <ButtonOld onClick={handleLoadMore} className="min-w-[120px]">
              Load More
            </ButtonOld>
          ) : (
            <div ref={ref} className="h-10" />
          )}
        </div>
      )}

      {!hasMoreImages && !hasNextPage && images?.length > 0 && (
        <div className="py-8 text-center text-gray-500 dark:text-dark-700">
          No more images to load
        </div>
      )}

      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Image</DialogTitle>
            <DialogDescription>
              Please provide a reason for reporting this image. This will be
              reviewed by our moderation team.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter your reason for reporting..."
            value={reportMessage}
            onChange={(e) => setReportMessage(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReportDialogOpen(false);
                setReportMessage('');
                setSelectedReport(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedReport) {
                  reportImage({
                    id: selectedReport.id,
                    imageIndex: selectedReport.index,
                    message: reportMessage,
                  });
                }
              }}
              disabled={isReporting || !reportMessage.trim()}
            >
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* <FullscreenImage
        prompt={selectedImage?.prompt}
        imageUrl={selectedImage?.url ?? null}
        onClose={() => {
          setSelectedImage(null);
          setFullscreenImage(null);
        }}
        onDownload={
          selectedImage
            ? (url) => handleDownload(url, `generated-image.jpg`)
            : undefined
        }
        onLike={
          selectedImage
            ? () => {
                toggleLike({
                  id: selectedImage.id,
                  imageIndex: selectedImage.index,
                });
                setSelectedImage((prev) =>
                  prev
                    ? {
                        ...prev,
                        isLiked: !prev.isLiked,
                      }
                    : null
                );
              }
            : undefined
        }
        onReport={
          selectedImage
            ? () => {
                setSelectedReport({
                  id: selectedImage.id,
                  index: selectedImage.index,
                });
                setReportDialogOpen(true);
              }
            : undefined
        }
        isLiked={selectedImage?.isLiked}
      /> */}
    </div>
  );
};

export const GeneratedImagesGrid: React.FC<GeneratedImagesGridProps> = ({
  myImages,
  publicImages,
  loading,
  setFullscreenImage,
  isGenerating,
  status,
  maxHeight,
  isGalleryPage = false,
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    myImages.images?.length ? 'my-images' : 'gallery'
  );

  useEffect(() => {
    if (isGenerating || status === 'pending') {
      setActiveTab('my-images');
    }
  }, [isGenerating, status]);

  // TODO: test this very carefully
  useEffect(() => {
    if (!myImages.isLoading && myImages.images.length > 0) {
      setActiveTab('my-images');
    }
  }, [myImages.isLoading, myImages.images.length]);

  if (loading) {
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mt-0 xl:mt-8">
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => setActiveTab(key.toString())}
        aria-label="Image tabs"
        color="primary"
        variant="underlined"
        classNames={{
          tabList: 'gap-4 xl:gap-6',
          cursor: 'bg-brand h-1 w-full',
          tab: 'h-10 px-0 font-medium text-sm xl:text-base',
        }}
      >
        <Tab
          key="my-images"
          title={
            <div className="flex items-center gap-2">
              <span>My Images</span>
              {myImages.totalCount > 0 && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-dark-400">
                  {myImages.totalCount}
                </span>
              )}
            </div>
          }
        >
          <div className="mt-4 px-2 xl:mt-6 xl:px-4">
            <ImageGrid
              {...myImages}
              setFullscreenImage={setFullscreenImage}
              maxHeight={maxHeight}
              isGalleryPage={isGalleryPage}
            />
          </div>
        </Tab>
        <Tab
          key="gallery"
          title={
            <div className="flex items-center gap-2">
              <span>Public Gallery</span>
              {publicImages.totalCount > 0 && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-dark-400">
                  {publicImages.totalCount}
                </span>
              )}
            </div>
          }
        >
          <div className="mt-4 px-2 xl:mt-6 xl:px-4">
            <ImageGrid
              {...publicImages}
              setFullscreenImage={setFullscreenImage}
              maxHeight={maxHeight}
              isGalleryPage={isGalleryPage}
            />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};
